import { useState, useEffect, useRef } from "react";
import { PatientMergeSearchResult } from "@Shape-Digital/kudzu-data/lib/types/actions";
import useFirebaseAppFunction from "../../../hooks/useFirebaseAppFunction";

export type { PatientMergeSearchResult };

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

const useSearchPatientsForMerge = (query: string) => {
  const [results, setResults] = useState<PatientMergeSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchPatientsForMerge = useFirebaseAppFunction(
    "searchPatientsForMerge",
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // See useSearchCustomersForMerge — same overlapping-request guard: a stale
  // response must not overwrite newer results or clear the loading flag
  // while the current request is still running.
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);

    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await searchPatientsForMerge({ query });
        if (requestIdRef.current !== requestId) return;
        setResults(data ?? []);
      } catch {
        if (requestIdRef.current !== requestId) return;
        setResults([]);
      } finally {
        if (requestIdRef.current === requestId) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, searchPatientsForMerge]);

  return { results, isLoading };
};

export default useSearchPatientsForMerge;
